import * as Sentry from '@sentry/react';
Sentry.init({
  dsn: 'https://eddb018724afc3032b02fb284020aaa5@o4505720317149184.ingest.us.sentry.io/4509629030924288',
  enabled: import.meta.env.VITE_SENTRY_ENABLED === 'true',
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  debug: true,
});

import { createRoot } from 'react-dom/client';
import './index.css';
import './utils/stringExtensions';

import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from 'src/auth/msalInstance';

import { MantineProvider } from '@mantine/core';
import { Box } from 'src/design/atoms/Box';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'src/api/queries/queryClient';
import { BrowserRouter } from 'react-router-dom';
import { AuthUserProvider } from 'src/hooks/useAuth';
import { VersionCheckProvider } from 'src/hooks/useVersionCheck';
import { VersionUpdateBanner } from 'src/app/VersionUpdateBanner';
import App from './App';
import { PathProvider } from './hooks/usePath';
import reportWebVitals from './reportWebVitals';
import './styles/convenience.css';

import mantineTheme from './themes/mantineTheme';

import '@mantine/core/styles.css';

import '@mantine/charts/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import 'mantine-react-table/styles.css';

import { Notifications } from '@mantine/notifications';
import { MenuProvider } from 'src/hooks/useMenu';

const root = createRoot(document.getElementById('root') as HTMLElement);

function GlobalErrorFallback({ error }: any) {
  return (
    <Box
      m="xl"
      style={{
        display: 'flex',
        alignContent: 'center',
        flexDirection: 'column',
      }}
    >
      <Box maw={1000}>
        <h1>Firetrol Insite</h1>
        <h2>Sorry, a fatal error occured.</h2>
        <p>
          Hi! Unfortunately, a fatal error occured while loading the
          application. This is likely an issue with the system and not due to
          your actions. If you see this error page, please try to refresh the
          page. If the error repeats itself, please copy the contents of the
          grey box below and share it with our support team. Thank you for your
          patience.
        </p>
      </Box>
      <Box mt="md" p="md" bg="gray.5" style={{ borderRadius: '3px' }}>
        <Box>
          <b>Error Message:</b> {error.message}
        </Box>
        <Box>
          <b>Error Details:</b>
        </Box>
        <Box>{JSON.stringify(error)}</Box>
      </Box>
    </Box>
  );
}

root.render(
  <div>
    <MantineProvider theme={mantineTheme}>
      <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
        <VersionCheckProvider>
          <MsalProvider instance={msalInstance}>
            <QueryClientProvider client={queryClient}>
              <AuthUserProvider>
                <PathProvider>
                  <BrowserRouter>
                    <MenuProvider>
                      <App />
                      <Notifications zIndex={1306} />
                      <VersionUpdateBanner />
                    </MenuProvider>
                  </BrowserRouter>
                </PathProvider>
              </AuthUserProvider>
            </QueryClientProvider>
          </MsalProvider>
        </VersionCheckProvider>
      </ErrorBoundary>
    </MantineProvider>
  </div>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
